// components/appointments/ParticipantsSection.js
// Sezione gestione partecipanti appuntamento - stile Teamleader Focus

import React, { useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Users, User, Mail, Phone, Plus, X, Building, 
  UserPlus, Calendar as CalendarIcon, AlertCircle 
} from 'lucide-react';

/**
 * ParticipantsSection - Gestione partecipanti appuntamento
 * 
 * @param {Object} lead - Lead/cliente principale
 * @param {Array} users - Lista utenti CRM selezionabili
 * @param {Array} internalParticipants - Partecipanti interni selezionati
 * @param {Array} externalParticipants - Partecipanti esterni (clienti/manuali)
 * @param {Function} onInternalChange - Callback per modifiche partecipanti interni
 * @param {Function} onExternalChange - Callback per modifiche partecipanti esterni
 * @param {boolean} googleConnected - Se Google Calendar è collegato
 * @param {boolean} sendClientEmail - Se inviare email al cliente principale
 * @param {Function} onSendClientEmailChange - Callback per checkbox email cliente
 */
export const ParticipantsSection = ({
  lead,
  users = [],
  internalParticipants = [],
  externalParticipants = [],
  onInternalChange,
  onExternalChange,
  googleConnected = false,
  sendClientEmail = false,
  onSendClientEmailChange
}) => {
  const [showAddInternal, setShowAddInternal] = useState(false);
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualParticipant, setManualParticipant] = useState({ name: '', email: '', phone: '' });
  const [selectedUserId, setSelectedUserId] = useState('');

  const hasClientEmail = !!lead?.email;

  // Filtra utenti già aggiunti
  const availableUsers = users.filter(u => 
    !internalParticipants.find(p => p.user_id === u.id)
  );

  // Aggiungi partecipante interno
  const handleAddInternal = () => {
    if (!selectedUserId) return;
    
    const user = users.find(u => u.id === selectedUserId);
    if (!user) return;

    const newParticipant = {
      user_id: user.id,
      name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      role: user.role,
      send_email: true,
      add_to_google: false
    };

    onInternalChange([...internalParticipants, newParticipant]);
    setSelectedUserId('');
    setShowAddInternal(false);
  };

  // Rimuovi partecipante interno
  const handleRemoveInternal = (userId) => {
    onInternalChange(internalParticipants.filter(p => p.user_id !== userId));
  };

  // Toggle send_email interno
  const handleToggleInternalEmail = (userId, checked) => {
    onInternalChange(internalParticipants.map(p => 
      p.user_id === userId ? { ...p, send_email: checked } : p
    ));
  };

  // Toggle add_to_google interno
  const handleToggleInternalGoogle = (userId, checked) => {
    onInternalChange(internalParticipants.map(p => 
      p.user_id === userId ? { ...p, add_to_google: checked } : p
    ));
  };

  // Aggiungi partecipante manuale
  const handleAddManual = () => {
    if (!manualParticipant.name || !manualParticipant.email) return;

    // Check duplicati
    const alreadyExists = externalParticipants.find(
      p => p.email?.toLowerCase() === manualParticipant.email.toLowerCase()
    );
    if (alreadyExists) return;

    const newParticipant = {
      name: manualParticipant.name,
      email: manualParticipant.email,
      phone: manualParticipant.phone || null,
      type: 'manual',
      send_email: true,
      add_to_google: false
    };

    onExternalChange([...externalParticipants, newParticipant]);
    setManualParticipant({ name: '', email: '', phone: '' });
    setShowAddManual(false);
  };

  // Rimuovi partecipante esterno
  const handleRemoveExternal = (email) => {
    onExternalChange(externalParticipants.filter(p => p.email !== email));
  };

  // Toggle send_email esterno
  const handleToggleExternalEmail = (email, checked) => {
    onExternalChange(externalParticipants.map(p => 
      p.email === email ? { ...p, send_email: checked } : p
    ));
  };

  // Toggle add_to_google esterno
  const handleToggleExternalGoogle = (email, checked) => {
    onExternalChange(externalParticipants.map(p => 
      p.email === email ? { ...p, add_to_google: checked } : p
    ));
  };

  return (
    <div className="space-y-4 pt-2 border-t">
      <Label className="text-sm font-medium flex items-center gap-2">
        <Users className="w-4 h-4" />
        Partecipanti
      </Label>

      {/* Cliente Principale */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#FF7819]" />
            <span className="font-medium text-sm">
              {lead?.first_name} {lead?.last_name}
            </span>
            <span className="text-xs bg-[#FF7819]/10 text-[#FF7819] px-2 py-0.5 rounded">
              Cliente
            </span>
          </div>
        </div>
        
        {hasClientEmail ? (
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {lead.email}
            </span>
            {lead?.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {lead.phone}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs text-amber-600">
            <AlertCircle className="w-3 h-3" />
            Email non disponibile
          </div>
        )}

        <div className="flex items-center gap-4 pt-1">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="client-send-email"
              checked={sendClientEmail}
              onCheckedChange={onSendClientEmailChange}
              disabled={!hasClientEmail}
              data-testid="client-send-email-checkbox"
            />
            <Label 
              htmlFor="client-send-email" 
              className={`text-xs cursor-pointer ${!hasClientEmail ? 'text-gray-400' : ''}`}
            >
              Invia email
            </Label>
          </div>
          
          {googleConnected && hasClientEmail && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="client-add-google"
                checked={false}
                disabled
                data-testid="client-add-google-checkbox"
              />
              <Label htmlFor="client-add-google" className="text-xs text-gray-400 cursor-not-allowed">
                Aggiungi a Google
              </Label>
            </div>
          )}
        </div>
      </div>

      {/* Partecipanti Interni (Colleghi) */}
      {internalParticipants.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-gray-500 uppercase tracking-wide">Colleghi</Label>
          {internalParticipants.map((participant) => (
            <div 
              key={participant.user_id}
              className="bg-blue-50 rounded-lg p-3 flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-sm">{participant.name}</span>
                  {participant.role && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">
                      {participant.role}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                  <Mail className="w-3 h-3" />
                  {participant.email}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={participant.send_email}
                      onCheckedChange={(checked) => handleToggleInternalEmail(participant.user_id, checked)}
                    />
                    <Label className="text-xs cursor-pointer">Invia email</Label>
                  </div>
                  {googleConnected && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={participant.add_to_google}
                        onCheckedChange={(checked) => handleToggleInternalGoogle(participant.user_id, checked)}
                      />
                      <Label className="text-xs cursor-pointer">Google Calendar</Label>
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveInternal(participant.user_id)}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Partecipanti Esterni (Manuali) */}
      {externalParticipants.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-gray-500 uppercase tracking-wide">Altri Partecipanti</Label>
          {externalParticipants.map((participant, idx) => (
            <div 
              key={participant.email || idx}
              className="bg-green-50 rounded-lg p-3 flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-sm">{participant.name}</span>
                  <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">
                    {participant.type === 'manual' ? 'Manuale' : 'Contatto'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                  {participant.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {participant.email}
                    </span>
                  )}
                  {participant.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {participant.phone}
                    </span>
                  )}
                </div>
                {participant.email && (
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={participant.send_email}
                        onCheckedChange={(checked) => handleToggleExternalEmail(participant.email, checked)}
                      />
                      <Label className="text-xs cursor-pointer">Invia email</Label>
                    </div>
                    {googleConnected && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={participant.add_to_google}
                          onCheckedChange={(checked) => handleToggleExternalGoogle(participant.email, checked)}
                        />
                        <Label className="text-xs cursor-pointer">Google Calendar</Label>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveExternal(participant.email)}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Pulsanti Aggiungi */}
      <div className="flex flex-wrap gap-2">
        {!showAddInternal && availableUsers.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddInternal(true)}
            className="text-xs"
            data-testid="add-internal-participant-btn"
          >
            <Building className="w-3 h-3 mr-1" />
            Aggiungi collega
          </Button>
        )}
        
        {!showAddManual && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddManual(true)}
            className="text-xs"
            data-testid="add-manual-participant-btn"
          >
            <UserPlus className="w-3 h-3 mr-1" />
            Aggiungi email
          </Button>
        )}
      </div>

      {/* Form Aggiungi Collega */}
      {showAddInternal && (
        <div className="bg-blue-50/50 rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Aggiungi collega</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddInternal(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger data-testid="select-internal-user">
              <SelectValue placeholder="Seleziona utente..." />
            </SelectTrigger>
            <SelectContent>
              {availableUsers.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            onClick={handleAddInternal}
            disabled={!selectedUserId}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi
          </Button>
        </div>
      )}

      {/* Form Aggiungi Manuale */}
      {showAddManual && (
        <div className="bg-green-50/50 rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Aggiungi partecipante</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddManual(false);
                setManualParticipant({ name: '', email: '', phone: '' });
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Input
            placeholder="Nome *"
            value={manualParticipant.name}
            onChange={(e) => setManualParticipant(prev => ({ ...prev, name: e.target.value }))}
            data-testid="manual-participant-name"
          />
          <Input
            type="email"
            placeholder="Email *"
            value={manualParticipant.email}
            onChange={(e) => setManualParticipant(prev => ({ ...prev, email: e.target.value }))}
            data-testid="manual-participant-email"
          />
          <Input
            placeholder="Telefono (opzionale)"
            value={manualParticipant.phone}
            onChange={(e) => setManualParticipant(prev => ({ ...prev, phone: e.target.value }))}
            data-testid="manual-participant-phone"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleAddManual}
            disabled={!manualParticipant.name || !manualParticipant.email}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi
          </Button>
        </div>
      )}
    </div>
  );
};

export default ParticipantsSection;
